import { CheckCircleIcon, QrCodeIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import QrScanner, { canScan } from "../components/QrScanner";
import { apiErrorMessage } from "../services/api";
import { fetchAppointment } from "../services/bookingService";
import { fetchBranches } from "../services/branchService";
import { formatDateTime } from "../services/datetime";
import { claimReward, fetchCard, lookupCard, redeemCode } from "../services/loyaltyService";
import { formatCode, formatCodeInput, normalizeCode } from "../services/shortCode";

/**
 * Loyalty → Redeem (`loyalty.redeem`) — the desk.
 *
 * Scan (camera, where the browser can) or type either code a customer shows:
 *  - their **card** code → the customer, their balance and the rewards they
 *    can have here; choosing one claims it and uses it in one go;
 *  - a **reward** code (a reward they already claimed in the app) → confirm
 *    and use it.
 * Opened from an appointment (`?client=&branch=&appointment=`) it starts on
 * that client's card and links the use to the visit. Every rule — enough
 * points, this branch, not expired — is the server's; its refusal is shown
 * inline as it comes back.
 */
export default function LoyaltyRedeem() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const appointmentId = params.get("appointment") || null;

  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(params.get("branch") || "");
  const [code, setCode] = useState("");
  const [scanning, setScanning] = useState(false);
  const [card, setCard] = useState(null);
  const [rewardCode, setRewardCode] = useState(""); // a typed code that is not a card
  // Awaiting confirmation: { kind: "claim", reward } or { kind: "use", reward, code }.
  const [pending, setPending] = useState(null);
  const [freeLine, setFreeLine] = useState(""); // a free service: the visit's line made free
  const [visitLines, setVisitLines] = useState([]);
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchBranches(1, 100)
      .then(({ branches: list }) => {
        setBranches(list ?? []);
        setBranchId((current) => current || list?.[0]?.id || "");
      })
      .catch(() => {});
  }, []);

  // Opened from an appointment: its lines, so a free service can name the one it covers.
  useEffect(() => {
    if (!appointmentId) return;
    fetchAppointment(appointmentId)
      .then((appointment) =>
        setVisitLines(
          (appointment.services ?? []).map((line) => ({
            id: line.id,
            serviceId: line.service_id,
            name: line.service?.name ?? "",
          }))
        )
      )
      .catch(() => {});
  }, [appointmentId]);

  /** A free service on this visit: the lines whose service the reward covers. */
  const matchingLines = (reward) =>
    reward?.type === "free_service"
      ? visitLines.filter((line) => (reward.services ?? []).some((s) => s.id === line.serviceId))
      : [];

  const ask = (next) => {
    setPending(next);
    setFreeLine(matchingLines(next.reward)[0]?.id ?? "");
  };

  // Opened from an appointment: start on that client's card.
  const clientParam = params.get("client");
  useEffect(() => {
    if (!clientParam) return;
    setBusy(true);
    fetchCard(clientParam, params.get("branch") || undefined)
      .then(setCard)
      .catch((err) => setError(apiErrorMessage(err, t("loyalty.loadFailed"))))
      .finally(() => setBusy(false));
  }, [clientParam, params, t]);

  const reset = () => {
    setCard(null);
    setRewardCode("");
    setPending(null);
    setReceipt(null);
    setError("");
    setCode("");
  };

  const find = useCallback(
    async (value) => {
      // Typed `k7m-4qx2`, scanned `K7M4QX2`: the API takes either; send it bare.
      const typed = normalizeCode(value ?? code);
      if (!typed) return;
      setBusy(true);
      setError("");
      setCard(null);
      setRewardCode("");
      setReceipt(null);
      try {
        setCard(await lookupCard(typed, branchId));
      } catch (err) {
        if (err.response?.status === 404) {
          // Not a card: treat it as a claimed reward's code, confirmed first.
          setRewardCode(typed);
        } else {
          setError(apiErrorMessage(err, t("loyalty.redeem.lookupFailed")));
        }
      } finally {
        setBusy(false);
      }
    },
    [code, branchId, t]
  );

  const onScanned = useCallback(
    (value) => {
      setScanning(false);
      setCode(formatCode(value));
      find(value);
    },
    [find]
  );

  /** Claim the chosen reward for the client and use it straight away. */
  const redeemReward = async (reward) => {
    setBusy(true);
    setError("");
    try {
      const claimed = await claimReward(card.member.client_id, { rewardId: reward.id, branchId });
      const used = await redeemCode({
        code: claimed.redemption.code,
        branchId,
        appointmentId,
        appointmentServiceId: freeLine,
      });
      setReceipt({
        reward: reward.name,
        code: used.code,
        customer: card.member.name,
        points: used.points_spent,
        stamps: used.stamps_spent,
        balance: claimed.balances?.points,
        at: used.redeemed_at,
      });
      setPending(null);
    } catch (err) {
      setError(apiErrorMessage(err, t("loyalty.redeem.failed")));
      setPending(null);
      // The balance may have moved (claimed but not used): show it fresh.
      if (card?.member?.client_id)
        fetchCard(card.member.client_id, branchId)
          .then(setCard)
          .catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  /** Use a reward code the customer already holds (claimed in the app or earlier). */
  const applyCode = async (value, appointmentServiceId) => {
    setBusy(true);
    setError("");
    try {
      const used = await redeemCode({ code: value, branchId, appointmentId, appointmentServiceId });
      setReceipt({
        reward: used.reward?.name,
        code: used.code,
        customer: card?.member?.name ?? null,
        points: used.points_spent,
        stamps: used.stamps_spent,
        balance: null,
        at: used.redeemed_at,
      });
      setRewardCode("");
      setPending(null);
    } catch (err) {
      setError(apiErrorMessage(err, t("loyalty.redeem.failed")));
      setPending(null);
    } finally {
      setBusy(false);
    }
  };

  const eligible = (card?.rewards ?? []).filter((reward) => reward.can_claim);
  const notYet = (card?.rewards ?? []).filter((reward) => !reward.can_claim);

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-5">
        <h1 className="text-xl font-bold">{t("loyalty.redeem.title")}</h1>
        <p className="text-sm text-ink-2">{t("loyalty.redeem.subtitle")}</p>
        {appointmentId && (
          <Link
            to={`/appointments/${appointmentId}`}
            className="text-sm font-medium text-accent-text hover:underline"
          >
            ← {t("loyalty.redeem.backToAppointment")}
          </Link>
        )}
      </div>

      {receipt ? (
        <div className="card p-6 text-center" role="status">
          <CheckCircleIcon className="mx-auto h-12 w-12 text-accent-text" aria-hidden="true" />
          <h2 className="mt-2 text-lg font-bold">{t("loyalty.redeem.success")}</h2>
          <dl className="mx-auto mt-4 max-w-sm space-y-1 text-start text-sm">
            {receipt.customer && <Row label={t("loyalty.redeem.customer")} value={receipt.customer} />}
            <Row label={t("loyalty.redeem.reward")} value={receipt.reward} />
            <Row
              label={t("loyalty.redeem.code")}
              value={
                <span className="font-mono" dir="ltr">
                  {formatCode(receipt.code)}
                </span>
              }
            />
            {receipt.points > 0 && (
              <Row label={t("loyalty.redeem.pointsSpent")} value={<span dir="ltr">{receipt.points}</span>} />
            )}
            {receipt.stamps > 0 && (
              <Row label={t("loyalty.redeem.stampsSpent")} value={<span dir="ltr">{receipt.stamps}</span>} />
            )}
            {receipt.balance != null && (
              <Row label={t("loyalty.redeem.newBalance")} value={<span dir="ltr">{receipt.balance}</span>} />
            )}
            {receipt.at && (
              <Row label={t("loyalty.redeem.when")} value={formatDateTime(receipt.at, i18n.language)} />
            )}
          </dl>
          <button type="button" className="btn-primary mt-6" onClick={reset}>
            {t("loyalty.redeem.next")}
          </button>
        </div>
      ) : (
        <>
          <form
            className="card mb-4 grid gap-3 p-4 sm:grid-cols-3"
            onSubmit={(e) => {
              e.preventDefault();
              find();
            }}
          >
            <div>
              <label className="field-label" htmlFor="rd-branch">
                {t("loyalty.redeem.branch")}
              </label>
              <select
                id="rd-branch"
                className="field-input"
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
              >
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="field-label" htmlFor="rd-code">
                {t("loyalty.redeem.code")}
              </label>
              <div className="flex gap-2">
                <input
                  id="rd-code"
                  dir="ltr"
                  autoComplete="off"
                  className="field-input flex-1 font-mono"
                  value={code}
                  // Upper-case, look-alike-free characters, the dash added
                  // as they type: K7M-4QX2.
                  onChange={(e) => setCode(formatCodeInput(e.target.value))}
                  placeholder={t("loyalty.redeem.codePlaceholder")}
                  autoCapitalize="characters"
                  spellCheck={false}
                />
                {canScan() && (
                  <button
                    type="button"
                    className="btn-secondary inline-flex items-center gap-1"
                    onClick={() => setScanning(true)}
                    aria-label={t("loyalty.scan.open")}
                  >
                    <QrCodeIcon className="h-5 w-5" aria-hidden="true" />
                    <span className="hidden sm:inline">{t("loyalty.scan.open")}</span>
                  </button>
                )}
                <button type="submit" className="btn-primary" disabled={busy || !code.trim()}>
                  {t("loyalty.redeem.find")}
                </button>
              </div>
              {!canScan() && <p className="mt-1 text-xs text-ink-2">{t("loyalty.scan.typeInstead")}</p>}
            </div>
          </form>

          {error && (
            <div
              className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text"
              role="alert"
            >
              {error}
            </div>
          )}

          {busy && !card && <p className="py-8 text-center text-sm text-ink-2">{t("common.loading")}</p>}

          {rewardCode && (
            <div className="card p-4">
              <p className="text-sm">
                {t("loyalty.redeem.rewardCodeIntro", { code: formatCode(rewardCode) })}
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  className="btn-primary"
                  disabled={busy}
                  onClick={() => applyCode(rewardCode)}
                >
                  {busy ? t("common.saving") : t("loyalty.redeem.applyCode")}
                </button>
                <button type="button" className="btn-secondary" onClick={reset} disabled={busy}>
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          )}

          {card && (
            <div className="space-y-4">
              <div className="card flex flex-wrap items-center justify-between gap-3 p-4">
                <div>
                  <p className="text-lg font-bold">{card.member?.name}</p>
                  <p className="text-sm text-ink-2">
                    {t("loyalty.pointsCount", { count: card.points.balance })}
                    {card.stamps?.required
                      ? ` · ${t("loyalty.card.stampsOf", { count: card.stamps.balance, required: card.stamps.required })}`
                      : ""}
                    {card.tier ? ` · ${card.tier.name}` : ""}
                  </p>
                </div>
                {card.member?.client_id && (
                  <Link to={`/loyalty/members/${card.member.client_id}`} className="btn-secondary">
                    {t("loyalty.redeem.openMember")}
                  </Link>
                )}
              </div>

              {card.outstanding?.length > 0 && (
                <section className="card p-4">
                  <h2 className="mb-2 font-semibold">{t("loyalty.card.outstanding")}</h2>
                  <ul className="divide-y divide-line">
                    {card.outstanding.map((row) => (
                      <li key={row.id} className="flex items-center justify-between gap-2 py-2">
                        <span>
                          {row.reward?.name}{" "}
                          <span className="font-mono text-xs text-ink-2" dir="ltr">
                            {row.code_display ?? formatCode(row.code)}
                          </span>
                        </span>
                        <button
                          type="button"
                          className="btn-primary"
                          disabled={busy}
                          // Several of the visit's services match a free
                          // service: ask which one first.
                          onClick={() =>
                            matchingLines(row.reward).length > 1
                              ? ask({ kind: "use", reward: row.reward, code: row.code })
                              : applyCode(row.code)
                          }
                        >
                          {t("loyalty.card.useNow")}
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <section className="card p-4">
                <h2 className="mb-2 font-semibold">{t("loyalty.redeem.eligible")}</h2>
                {eligible.length === 0 ? (
                  <p className="text-sm text-ink-2">{t("loyalty.redeem.noneEligible")}</p>
                ) : (
                  <ul className="divide-y divide-line">
                    {eligible.map((reward) => (
                      <li key={reward.id} className="flex items-center justify-between gap-2 py-2">
                        <div>
                          <p className="font-medium">{reward.name}</p>
                          <p className="text-xs text-ink-2">
                            {[
                              reward.points_required > 0 &&
                                t("loyalty.pointsCount", { count: reward.points_required }),
                              reward.stamps_required > 0 &&
                                t("loyalty.stampsCount", { count: reward.stamps_required }),
                            ]
                              .filter(Boolean)
                              .join(" + ")}
                          </p>
                        </div>
                        <button
                          type="button"
                          className="btn-primary"
                          disabled={busy}
                          onClick={() => ask({ kind: "claim", reward })}
                        >
                          {t("loyalty.redeem.redeem")}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {notYet.length > 0 && (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-sm text-ink-2">
                      {t("loyalty.redeem.notYet", { count: notYet.length })}
                    </summary>
                    <ul className="mt-2 space-y-1 text-sm">
                      {notYet.map((reward) => (
                        <li key={reward.id} className="flex justify-between gap-2">
                          <span>{reward.name}</span>
                          <span className="text-warning-text">
                            {t(`loyalty.blocked.${reward.blocked_by?.[0] ?? "unavailable"}`, {
                              points: reward.points_short,
                              stamps: reward.stamps_short,
                            })}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </section>
            </div>
          )}
        </>
      )}

      {pending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="rd-confirm"
            className="card w-full max-w-sm p-5"
          >
            <h2 id="rd-confirm" className="font-semibold">
              {t("loyalty.redeem.confirmTitle")}
            </h2>
            <p className="mt-2 text-sm text-ink-2">
              {t(pending.kind === "claim" ? "loyalty.redeem.confirmBody" : "loyalty.redeem.confirmUseBody", {
                reward: pending.reward.name,
                name: card?.member?.name,
              })}
            </p>
            {matchingLines(pending.reward).length > 1 && (
              <div className="mt-3">
                <label className="field-label" htmlFor="rd-free-line">
                  {t("loyalty.redeem.freeLine")}
                </label>
                <select
                  id="rd-free-line"
                  className="field-input"
                  value={freeLine}
                  onChange={(e) => setFreeLine(e.target.value)}
                >
                  {matchingLines(pending.reward).map((line) => (
                    <option key={line.id} value={line.id}>
                      {line.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-ink-2">{t("loyalty.redeem.freeLineHint")}</p>
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setPending(null)}
                disabled={busy}
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() =>
                  pending.kind === "claim" ? redeemReward(pending.reward) : applyCode(pending.code, freeLine)
                }
                disabled={busy}
              >
                {busy ? t("common.saving") : t("loyalty.redeem.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      <QrScanner open={scanning} onDetected={onScanned} onClose={() => setScanning(false)} />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ink-2">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}
